import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

const TournamentsSection = () => {
  const [tournaments, setTournaments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [loadingError, setLoadingError] = useState(null);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('');
  const [editingTournament, setEditingTournament] = useState(null);

  const [newTournament, setNewTournament] = useState({
    name: '',
    tournament_type: 'league',
    category_id: '',
    start_date: '',
    end_date: '',
    location: '',
    description: '',
    status: 'upcoming'
  });

  useEffect(() => {
    console.log('TournamentsSection component mounted, fetching data...');
    fetchAllData();
  }, []);

  // Debug logging for state changes
  useEffect(() => {
    console.log('Tournaments state updated:', tournaments.length, 'tournaments');
  }, [tournaments]);

  useEffect(() => {
    console.log('Categories state updated:', categories.length, 'categories');
  }, [categories]);

  const fetchAllData = async () => {
    setLoading(true);
    setLoadingError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('لم يتم العثور على رمز المصادقة. يرجى تسجيل الدخول مرة أخرى.');
      }
      
      const headers = { 'Authorization': `Bearer ${token}` };

      // جلب جميع البطولات (حتى المخفية) لإمكانية الحذف اليدوي
      const [tournamentsRes, categoriesRes] = await Promise.allSettled([
        axios.get(`${API}/api/tournaments?show_all=true`, { headers }),
        axios.get(`${API}/api/categories`, { headers })
      ]);

      if (tournamentsRes.status === 'fulfilled') {
        console.log('Tournaments response:', tournamentsRes.value.data);
        // Handle both direct array and wrapped object format
        const tournamentsData = Array.isArray(tournamentsRes.value.data) 
          ? tournamentsRes.value.data 
          : (tournamentsRes.value.data.tournaments || []);
        setTournaments(tournamentsData);
        console.log('Tournaments set to state:', tournamentsData.length, 'tournaments');
      } else {
        console.error('Failed to fetch tournaments:', tournamentsRes.reason);
        setTournaments([]);
      }

      if (categoriesRes.status === 'fulfilled') {
        console.log('Categories response:', categoriesRes.value.data);
        // Handle both direct array and wrapped object format  
        const categoriesData = Array.isArray(categoriesRes.value.data)
          ? categoriesRes.value.data
          : (categoriesRes.value.data.categories || []);
        setCategories(categoriesData);
        console.log('Categories set to state:', categoriesData.length, 'categories');
      } else {
        console.error('Failed to fetch categories:', categoriesRes.reason);
        setCategories([]);
      }
      
      // Show error if both requests failed
      if (tournamentsRes.status === 'rejected' && categoriesRes.status === 'rejected') {
        setLoadingError('فشل في تحميل البيانات. يرجى المحاولة مرة أخرى.');
      }
    } catch (error) {
      console.error('خطأ في جلب البيانات:', error);
      setLoadingError(error.message || 'حدث خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTournament = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/tournaments`, newTournament, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-xl shadow-lg z-50';
      notification.innerHTML = `
        <div class="flex items-center">
          <span class="text-xl mr-2">🏆</span>
          <span>تم إضافة البطولة بنجاح!</span>
        </div>
      `;
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 4000);

      // Reset form and close modal
      setNewTournament({
        name: '',
        tournament_type: 'league',
        category_id: '',
        start_date: '',
        end_date: '',
        location: '',
        description: '',
        status: 'upcoming'
      });
      setShowAddModal(false);
      fetchAllData();
    } catch (error) {
      console.error('خطأ في إضافة البطولة:', error);
      alert('حدث خطأ في إضافة البطولة');
    } finally {
      setSaving(false);
    }
  };

  const handleEditTournament = (tournament) => {
    setEditingTournament({
      ...tournament,
      start_date: tournament.start_date?.split('T')[0] || '',
      end_date: tournament.end_date?.split('T')[0] || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateTournament = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/tournaments/${editingTournament.id}`, editingTournament, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-xl shadow-lg z-50';
      notification.innerHTML = `
        <div class="flex items-center">
          <span class="text-xl mr-2">✏️</span>
          <span>تم تحديث البطولة بنجاح!</span>
        </div>
      `;
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 4000);

      setShowEditModal(false);
      setEditingTournament(null);
      fetchAllData();
    } catch (error) {
      console.error('خطأ في تحديث البطولة:', error);
      alert('حدث خطأ في تحديث البطولة');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTournament = async (tournamentId, tournamentName) => {
    if (!window.confirm(`هل أنت متأكد من حذف البطولة "${tournamentName}"؟`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/tournaments/${tournamentId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-xl shadow-lg z-50';
      notification.innerHTML = `
        <div class="flex items-center">
          <span class="text-xl mr-2">🗑️</span>
          <span>تم حذف البطولة بنجاح!</span>
        </div>
      `;
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 4000);

      fetchAllData();
    } catch (error) {
      console.error('خطأ في حذف البطولة:', error);
      alert('حدث خطأ في حذف البطولة');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'league': return '🏆';
      case 'cup': return '🥇';
      case 'friendly': return '🤝';
      default: return '🏆';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'upcoming': return '📅';
      case 'ongoing': return '▶️';
      case 'completed': return '✅';
      case 'cancelled': return '❌';
      default: return '📅';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'bg-blue-100 text-blue-800';
      case 'ongoing': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">جاري تحميل البطولات والفئات...</p>
        </div>
      </div>
    );
  }

  if (loadingError) {
    return (
      <div className="text-center py-8">
        <div className="text-4xl mb-4">⚠️</div>
        <h3 className="text-lg font-semibold text-red-600 mb-2">خطأ في التحميل</h3>
        <p className="text-gray-600 mb-4">{loadingError}</p>
        <button
          onClick={fetchAllData}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
        >
          إعادة المحاولة
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">🏆 إدارة البطولات</h1>
          <p className="text-gray-600 mt-1">تسجيل وإدارة البطولات التي يشارك فيها النادي</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center"
        >
          + إضافة بطولة جديدة
        </button>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-md p-4 border-r-4 border-indigo-500">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-1">إجمالي البطولات</h4>
              <p className="text-2xl font-bold text-gray-900">{tournaments.length}</p>
            </div>
            <div className="p-3 rounded-full bg-indigo-50">
              <span className="text-xl">🏆</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-r-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-1">البطولات الجارية</h4>
              <p className="text-2xl font-bold text-gray-900">{tournaments.filter(t => t.status === 'ongoing').length}</p>
            </div>
            <div className="p-3 rounded-full bg-green-50">
              <span className="text-xl">▶️</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-r-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-1">البطولات القادمة</h4>
              <p className="text-2xl font-bold text-gray-900">{tournaments.filter(t => t.status === 'upcoming').length}</p>
            </div>
            <div className="p-3 rounded-full bg-blue-50">
              <span className="text-xl">📅</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-r-4 border-gray-500">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-1">البطولات المكتملة</h4>
              <p className="text-2xl font-bold text-gray-900">{tournaments.filter(t => t.status === 'completed').length}</p>
            </div>
            <div className="p-3 rounded-full bg-gray-50">
              <span className="text-xl">✅</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tournaments List */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h4 className="text-xl font-bold text-gray-800">قائمة البطولات</h4>
        </div>

        {/* Category Filter */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center space-x-4 space-x-reverse">
            <div className="flex items-center">
              <span className="text-lg mr-2">🏆</span>
              <label className="text-sm font-semibold text-gray-700">فلترة حسب الفئة:</label>
            </div>
            <select
              value={selectedCategoryFilter}
              onChange={(e) => setSelectedCategoryFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
            >
              <option value="">جميع الفئات</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name} ({category.min_age}-{category.max_age} سنة)
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="p-6">
          {tournaments.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">🏆</div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">لا توجد بطولات</h3>
              <p className="text-gray-500 mb-4">ابدأ بإضافة البطولات التي يشارك فيها النادي</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                + إضافة بطولة جديدة
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {tournaments.map((tournament) => {
                const category = categories.find(cat => cat.id === tournament.category_id);
                return (
                  <div key={tournament.id} className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-6 border hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3 space-x-reverse">
                        <div className="text-2xl">{getTypeIcon(tournament.tournament_type)}</div>
                        <div>
                          <h5 className="text-lg font-bold text-gray-900">{tournament.name}</h5>
                          <p className="text-sm text-gray-600">{category?.name || 'غير محدد'}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(tournament.status)}`}>
                        {getStatusIcon(tournament.status)} {tournament.status === 'upcoming' ? 'قادمة' :
                                                       tournament.status === 'ongoing' ? 'جارية' :
                                                       tournament.status === 'completed' ? 'مكتملة' : 'ملغية'}
                      </span>
                    </div>

                    {tournament.description && (
                      <p className="text-gray-600 mb-3 text-sm line-clamp-2">{tournament.description}</p>
                    )}

                    <div className="space-y-2 text-sm text-gray-500 mb-4">
                      <div className="flex items-center">
                        <span className="text-blue-500 ml-2">📅</span>
                        <span>البداية: {new Date(tournament.start_date).toLocaleDateString('ar-SA')}</span>
                      </div>
                      {tournament.end_date && (
                        <div className="flex items-center">
                          <span className="text-red-500 ml-2">📅</span>
                          <span>النهاية: {new Date(tournament.end_date).toLocaleDateString('ar-SA')}</span>
                        </div>
                      )}
                      {tournament.location && (
                        <div className="flex items-center">
                          <span className="text-green-500 ml-2">📍</span>
                          <span>{tournament.location}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end space-x-2 space-x-reverse">
                      <button
                        onClick={() => handleEditTournament(tournament)}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium px-3 py-1 rounded hover:bg-blue-50 transition-colors"
                      >
                        ✏️ تعديل
                      </button>
                      <button
                        onClick={() => handleDeleteTournament(tournament.id, tournament.name)}
                        className="text-red-600 hover:text-red-700 text-sm font-medium px-3 py-1 rounded hover:bg-red-50 transition-colors"
                      >
                        🗑️ حذف
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Add Tournament Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white p-6 rounded-t-2xl">
              <h2 className="text-2xl font-bold">🏆 إضافة بطولة جديدة</h2>
            </div>

            <form onSubmit={handleAddTournament} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">اسم البطولة *</label>
                <input
                  type="text"
                  required
                  value={newTournament.name}
                  onChange={(e) => setNewTournament({ ...newTournament, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">نوع البطولة</label>
                  <select
                    value={newTournament.tournament_type}
                    onChange={(e) => setNewTournament({ ...newTournament, tournament_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="league">🏆 دوري</option>
                    <option value="cup">🥇 كأس</option>
                    <option value="friendly">🤝 ودية</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">الفئة *</label>
                  <select
                    required
                    value={newTournament.category_id}
                    onChange={(e) => setNewTournament({ ...newTournament, category_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">اختر الفئة</option>
                    {categories.length === 0 ? (
                      <option value="" disabled>لا توجد فئات متاحة</option>
                    ) : (
                      categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))
                    )}
                  </select>
                  {categories.length === 0 && (
                    <p className="text-sm text-red-600 mt-1">
                      لم يتم العثور على فئات. تأكد من إضافة الفئات في قسم إدارة الفئات.
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">تاريخ البدء *</label>
                  <input
                    type="date"
                    required
                    value={newTournament.start_date}
                    onChange={(e) => setNewTournament({ ...newTournament, start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">تاريخ الانتهاء</label>
                  <input
                    type="date"
                    value={newTournament.end_date}
                    onChange={(e) => setNewTournament({ ...newTournament, end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">المكان</label>
                <input
                  type="text"
                  value={newTournament.location}
                  onChange={(e) => setNewTournament({ ...newTournament, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="مثال: ملعب الملك فهد الدولي"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">وصف البطولة</label>
                <textarea
                  value={newTournament.description}
                  onChange={(e) => setNewTournament({ ...newTournament, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  rows={3}
                />
              </div>

              <div className="flex space-x-4 space-x-reverse">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                >
                  {saving ? 'جاري الحفظ...' : 'إضافة البطولة'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 px-4 rounded-lg font-medium transition-colors"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Tournament Modal */}
      {showEditModal && editingTournament && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">✏️ تعديل البطولة</h2>
            <form onSubmit={handleUpdateTournament} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">اسم البطولة *</label>
                <input
                  type="text"
                  required
                  value={editingTournament.name}
                  onChange={(e) => setEditingTournament({ ...editingTournament, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">نوع البطولة</label>
                  <select
                    value={editingTournament.tournament_type}
                    onChange={(e) => setEditingTournament({ ...editingTournament, tournament_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="league">🏆 دوري</option>
                    <option value="cup">🥇 كأس</option>
                    <option value="friendly">🤝 ودية</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">الفئة *</label>
                  <select
                    required
                    value={editingTournament.category_id}
                    onChange={(e) => setEditingTournament({ ...editingTournament, category_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">اختر الفئة</option>
                    {categories.length === 0 ? (
                      <option value="" disabled>لا توجد فئات متاحة</option>
                    ) : (
                      categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))
                    )}
                  </select>
                  {categories.length === 0 && (
                    <p className="text-sm text-red-600 mt-1">
                      لم يتم العثور على فئات. تأكد من إضافة الفئات في قسم إدارة الفئات.
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">تاريخ البدء *</label>
                  <input
                    type="date"
                    required
                    value={editingTournament.start_date?.split('T')[0] || ''}
                    onChange={(e) => setEditingTournament({ ...editingTournament, start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">تاريخ الانتهاء</label>
                  <input
                    type="date"
                    value={editingTournament.end_date?.split('T')[0] || ''}
                    onChange={(e) => setEditingTournament({ ...editingTournament, end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">المكان</label>
                <input
                  type="text"
                  value={editingTournament.location || ''}
                  onChange={(e) => setEditingTournament({ ...editingTournament, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="مثال: ملعب الملك فهد الدولي"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">وصف البطولة</label>
                <textarea
                  value={editingTournament.description || ''}
                  onChange={(e) => setEditingTournament({ ...editingTournament, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">حالة البطولة</label>
                <select
                  value={editingTournament.status}
                  onChange={(e) => setEditingTournament({ ...editingTournament, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="upcoming">📅 قادمة</option>
                  <option value="ongoing">▶️ جارية</option>
                  <option value="completed">✅ مكتملة</option>
                  <option value="cancelled">❌ ملغية</option>
                </select>
              </div>

              <div className="flex space-x-4 space-x-reverse">
                <button
                  type="submit"
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                >
                  تحديث البطولة
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingTournament(null);
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 px-4 rounded-lg font-medium transition-colors"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TournamentsSection;